<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content modal-gallery">
        <div class="modal-header">
          <h3>{{ currentImageIndex + 1 }} / {{ images.length }}</h3>
          <div class="header-actions">
            <a
              v-if="currentImage"
              :href="currentImage.downloadUrl"
              class="btn-icon-action"
              :title="s__('ImageGallery|Download image')"
              download
            >
              <DownloadIcon :size="16" />
            </a>
            <button type="button" class="modal-close" @click="handleClose">×</button>
          </div>
        </div>

        <div
          class="modal-body modal-gallery-body"
          :class="{ 'has-transparent-image': isTransparentImage }"
        >
          <button
            v-if="images.length > 1"
            type="button"
            class="gallery-nav-btn gallery-nav-prev"
            :title="s__('ImageGallery|Previous image')"
            @click="goToPrevious"
          >
            ‹
          </button>

          <div class="gallery-image-container">
            <img
              v-if="currentImage"
              :src="currentImage.downloadUrl"
              :alt="currentImage.filename"
              class="gallery-image"
              @load="handleImageLoad"
              @error="handleImageError"
            />
            <div v-if="imageLoading" class="gallery-loading">
              {{ s__('ImageGallery|Loading...') }}
            </div>
            <div v-if="imageError" class="gallery-error">
              {{ s__('ImageGallery|Failed to load image') }}
            </div>
          </div>

          <button
            v-if="images.length > 1"
            type="button"
            class="gallery-nav-btn gallery-nav-next"
            :title="s__('ImageGallery|Next image')"
            @click="goToNext"
          >
            ›
          </button>
        </div>

        <div v-if="currentImage" class="modal-footer">
          <p class="gallery-filename">{{ currentImage.filename }}</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useBodyScrollLock } from '~/composables/useBodyScrollLock'
import { s__ } from '~/locale'
import DownloadIcon from '~/components/common/icons/DownloadIcon.vue'

export default {
  name: 'ImageGalleryModal',
  components: {
    DownloadIcon,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    images: {
      type: Array,
      required: true,
      validator: value => {
        return Array.isArray(value) && value.length > 0
      },
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const currentIndex = ref(props.initialIndex)
    const imageLoading = ref(true)
    const imageError = ref(false)

    const isOpen = computed(() => props.visible)

    // Use body scroll lock composable
    useBodyScrollLock(isOpen)

    const currentImage = computed(() => {
      return props.images[currentIndex.value] || null
    })

    const currentImageIndex = computed(() => currentIndex.value)

    const isTransparentImage = computed(() => {
      if (!currentImage.value) return false

      const contentType = currentImage.value.contentType || ''
      const filename = currentImage.value.filename || ''

      // Check content type for potentially transparent formats
      if (
        contentType.includes('png') ||
        contentType.includes('svg') ||
        contentType.includes('webp')
      ) {
        return true
      }

      // Check filename extension as fallback
      const ext = filename.split('.').pop()?.toLowerCase()
      return ['png', 'svg', 'webp'].includes(ext)
    })

    const goToPrevious = () => {
      if (props.images.length <= 1) return
      currentIndex.value =
        currentIndex.value === 0 ? props.images.length - 1 : currentIndex.value - 1
      imageLoading.value = true
      imageError.value = false
    }

    const goToNext = () => {
      if (props.images.length <= 1) return
      currentIndex.value = (currentIndex.value + 1) % props.images.length
      imageLoading.value = true
      imageError.value = false
    }

    const handleClose = () => {
      emit('close')
    }

    const handleImageLoad = () => {
      imageLoading.value = false
      imageError.value = false
    }

    const handleImageError = () => {
      imageLoading.value = false
      imageError.value = true
    }

    // Handle keyboard navigation
    const handleKeyDown = event => {
      if (!props.visible) return

      if (event.key === 'Escape') {
        handleClose()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNext()
      }
    }

    // Watch for initialIndex changes
    watch(
      () => props.initialIndex,
      newIndex => {
        currentIndex.value = newIndex
        imageLoading.value = true
        imageError.value = false
      }
    )

    // Watch for image changes
    watch(
      () => currentImage.value,
      () => {
        imageLoading.value = true
        imageError.value = false
      }
    )

    onMounted(() => {
      document.addEventListener('keydown', handleKeyDown)
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown)
    })

    return {
      currentImage,
      currentImageIndex,
      isTransparentImage,
      imageLoading,
      imageError,
      goToPrevious,
      goToNext,
      handleClose,
      handleImageLoad,
      handleImageError,
      s__,
    }
  },
}
</script>

<style scoped>
.modal-gallery {
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  display: flex;
  flex-direction: column;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-icon-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: #757575;
  cursor: pointer;
  border-radius: 4px;
  transition:
    background-color 0.2s,
    color 0.2s;
  text-decoration: none;
}

.btn-icon-action:hover {
  background-color: #f0f0f0;
  color: #424242;
}

.modal-gallery-body {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-height: 400px;
  background: #fff;
  overflow: hidden;
}

/* Checkered pattern for transparent images only */
.modal-gallery-body.has-transparent-image::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #fff;
  background-image:
    linear-gradient(45deg, #ccc 25%, transparent 25%),
    linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%),
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0px;
  opacity: 0.5;
  z-index: 0;
}

.gallery-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  z-index: 1;
}

.gallery-image {
  max-width: 100%;
  max-height: calc(90vh - 120px);
  object-fit: contain;
  display: block;
}

.gallery-loading,
.gallery-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 16px;
}

.gallery-error {
  color: #dc2626;
}

.gallery-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #ffffff;
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.2s;
  color: #1f2937;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 0;
  margin: 0;
  font-size: 32px;
  font-weight: 300;
  line-height: 1;
  text-align: center;
}

.gallery-nav-btn:hover {
  background: #f9fafb;
  color: #111827;
}

.gallery-nav-prev {
  left: 20px;
  padding-right: 3px;
  padding-bottom: 3px;
}

.gallery-nav-next {
  right: 20px;
  padding-bottom: 3px;
}

.gallery-filename {
  margin: 0;
  font-size: 14px;
  color: #666;
  text-align: center;
  word-break: break-all;
}
</style>
